import { useMemo, useState, useEffect } from "react";
import { format as dateFormat } from "date-fns";
import DataTable from "../Table/Datatable";
import { DOMAIN_API_HOST } from "../../../configs/main";
import { useSnackbar } from "notistack";
import "./AgencyFinance.scss";

const getToken = () => {
  return localStorage.getItem("agency_token");
};

const fetchBalanceSummary = async () => {
  const token = getToken();
  const response = await fetch(`${DOMAIN_API_HOST}/api/v1/agency/balance-summary`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: "Ошибка при загрузке баланса" }));
    throw new Error(errorData.detail || "Ошибка при загрузке баланса");
  }

  return response.json();
};

const fetchTransactions = async (params) => {
  const token = getToken();
  const queryParams = new URLSearchParams();

  if (params.page !== undefined) queryParams.append("page", params.page);
  if (params.limit !== undefined) queryParams.append("limit", params.limit);

  const response = await fetch(`${DOMAIN_API_HOST}/api/v1/agency/transactions?${queryParams.toString()}`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: "Ошибка при загрузке транзакций" }));
    throw new Error(errorData.detail || "Ошибка при загрузке транзакций");
  }

  return response.json();
};

const getTransactionTypeLabel = (type) => {
  const typeMap = {
    DEPOSIT: "Пополнение",
    CHARGE: "Списание кампанией",
  };
  return typeMap[type] || type;
};

const getTransactionDirection = (type) => {
  const directionMap = {
    DEPOSIT: "Входящее",
    CHARGE: "Расход",
  };
  return directionMap[type] || "-";
};

const columns = [
  { Header: "ID операции", accessor: "id", width: 140, minWidth: 80, maxWidth: 300 },
  { Header: "Дата", accessor: "date", width: 170, minWidth: 80, maxWidth: 400 },
  { Header: "Тип операции", accessor: "type", width: 240, minWidth: 80, maxWidth: 400 },
  { Header: "Описание", accessor: "description", width: 340, minWidth: 120, maxWidth: 500 },
  { Header: "Направление", accessor: "direction", width: 150, minWidth: 80, maxWidth: 300 },
  { Header: "Сумма", accessor: "amount", width: 140, minWidth: 80, maxWidth: 300 },
];

function AgencyFinance() {
  const { enqueueSnackbar } = useSnackbar();
  const ITEMS_PER_PAGE = 100;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState({
    balance: 0,
    inCampaigns: 0,
    spent: 0,
  });

  useEffect(() => {
    const loadBalanceSummary = async () => {
      try {
        const data = await fetchBalanceSummary();
        setSummary({
          balance: data.agency_balance || 0,
          inCampaigns: data.clients_balance_sum || 0,
          spent: data.clients_deposits_sum || 0,
        });
      } catch (error) {
        enqueueSnackbar(error.message || "Ошибка при загрузке баланса", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    };

    loadBalanceSummary();
  }, [enqueueSnackbar]);

  useEffect(() => {
    const loadTransactions = async () => {
      setIsLoading(true);
      try {
        const params = {
          page: currentPage,
          limit: ITEMS_PER_PAGE,
        };

        const response = await fetchTransactions(params);
        const data = Array.isArray(response.data) ? response.data : [];

        setTransactions(data);
        setTotalPages(Math.max(1, response.total_pages || 1));
      } catch (error) {
        enqueueSnackbar(error.message || "Ошибка при загрузке транзакций", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        setTransactions([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadTransactions();
  }, [currentPage, enqueueSnackbar]);

  const tableData = useMemo(
    () =>
      transactions.map((tx) => {
        const amount = parseFloat(tx.amount || 0);
        const isNegative = tx.type === "CHARGE";
        const displayAmount = Math.abs(amount);

        return {
          id: <div className="td">{String(tx.id).slice(0, 8)}</div>,
          date: (
            <div className="td">
              {tx.created_at ? dateFormat(new Date(tx.created_at), "dd.MM.yyyy HH:mm") : "-"}
            </div>
          ),
          type: <div className="td">{getTransactionTypeLabel(tx.type)}</div>,
          description: <div className="td">{tx.description || "-"}</div>,
          direction: <div className="td">{getTransactionDirection(tx.type)}</div>,
          amount: (
            <div className="td" style={{ color: isNegative ? "#d32f2f" : "#2e7d32" }}>
              {isNegative ? "-" : "+"}
              {displayAmount.toLocaleString("ru-RU")} ₽
            </div>
          ),
        };
      }),
    [transactions]
  );

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
    if (direction === "next" && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  return (
    <div className="AgencyFinancePage">
      <h2>Финансы</h2>

      <div className="AgencyFinancePage-summary">
        <div className="summary-card">
          <div className="summary-card_header">
            <span>Текущий баланс агентства</span>
            <button type="button">Пополнить</button>
          </div>
          <p style={{ fontSize: "28px", fontWeight: "600" }}>{summary.balance.toLocaleString("ru-RU")} ₽</p>
        </div>
        <div className="summary-card">
          <div className="summary-card_header">
            <span>Текущий баланс клиентов</span>
          </div>
          <p style={{ fontSize: "28px", fontWeight: "600" }}>{summary.inCampaigns.toLocaleString("ru-RU")} ₽</p>
        </div>
        <div className="summary-card">
          <div className="summary-card_header">
            <span>Сумма пополнений клиентов</span>
          </div>
          <p style={{ fontSize: "28px", fontWeight: "600" }}>{summary.spent.toLocaleString("ru-RU")} ₽</p>
        </div>
      </div>

      <div className="AgencyFinancePage-table">
        <div className="AgencyFinancePage-tableCard">
          <div className="AgencyFinancePage-tableHeader">
            <div>
              <h3 style={{ fontSize: "22px", fontWeight: "600" }}>История операций</h3>
              <span style={{ fontSize: "15px", color: "rgba(0,0,0,0.55)", marginTop: "5px" }}>Пополнения, перечисления и списания внутри агентства</span>
            </div>
          </div>
          <div className="AgencyFinancePage-tableScroll" style={{ marginTop: "10px" }}>
            {isLoading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
                <p>Загрузка...</p>
              </div>
            ) : transactions.length === 0 ? (
              <div
                style={{
                  minHeight: "200px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "rgba(0,0,0,0.6)",
                  fontSize: 16,
                }}
              >
                Транзакций пока нет
              </div>
            ) : (
              <DataTable data={tableData} columns={columns} />
            )}
          </div>
          {!isLoading && transactions.length > 0 && totalPages > 1 && (
            <div className="AgencyFinancePage-pagination">
              <button onClick={() => handlePageChange("prev")} disabled={currentPage === 1}>
                Назад
              </button>
              <strong>
                {currentPage} / {totalPages}
              </strong>
              <button onClick={() => handlePageChange("next")} disabled={currentPage === totalPages}>
                Дальше
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AgencyFinance;

